import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, TrendingUp, Users, Eye, Monitor, Smartphone } from 'lucide-react';
import { API_URL } from '@/utils/api';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/analytics/data?days=${dateRange}`
      );
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <Link to="/admin">
              <Button variant="outline" size="sm" className="mb-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Visitor Analytics</h1>
            <p className="text-gray-600 mt-2">Track your website performance and visitor behavior</p>
          </div>
          
          {/* Date Range Selector */}
          <div className="flex gap-2">
            <Button
              variant={dateRange === 7 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(7)}
            >
              7 Days
            </Button>
            <Button
              variant={dateRange === 30 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(30)}
            >
              30 Days
            </Button>
            <Button
              variant={dateRange === 90 ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateRange(90)}
            >
              90 Days
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Visits</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analyticsData?.total_visits || 0}</div>
              <p className="text-xs text-muted-foreground">Last {dateRange} days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unique Visitors</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analyticsData?.unique_visitors || 0}</div>
              <p className="text-xs text-muted-foreground">Distinct visitor sessions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg. Daily Visits</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {analyticsData?.visits_per_day?.length > 0
                  ? Math.round(analyticsData.total_visits / analyticsData.visits_per_day.length)
                  : 0}
              </div>
              <p className="text-xs text-muted-foreground">Per day average</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts and Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Visits Per Day Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Visits Per Day</CardTitle>
              <CardDescription>Daily visitor traffic over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analyticsData?.visits_per_day?.slice(-14).map((day, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-24 text-sm text-gray-600">{day.date}</div>
                    <div className="flex-1 ml-4">
                      <div className="bg-gray-200 rounded-full h-6 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full rounded-full flex items-center justify-end pr-2"
                          style={{
                            width: `${Math.min((day.visits / Math.max(...analyticsData.visits_per_day.map(d => d.visits))) * 100, 100)}%`
                          }}
                        >
                          <span className="text-xs text-white font-medium">{day.visits}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Device Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Device Breakdown</CardTitle>
              <CardDescription>Visitors by device type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData?.device_breakdown?.map((device, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {device.device === 'mobile' ? (
                        <Smartphone className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Monitor className="h-5 w-5 text-green-600" />
                      )}
                      <span className="font-medium capitalize">{device.device}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-gray-600">
                        {Math.round((device.visits / analyticsData.total_visits) * 100)}%
                      </div>
                      <div className="text-lg font-bold">{device.visits}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Most Viewed Pages */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Most Viewed Pages</CardTitle>
            <CardDescription>Top 10 pages by visitor count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData?.most_viewed_pages?.slice(0, 10).map((page, index) => (
                <div key={index} className="flex items-center justify-between border-b border-gray-200 pb-3 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-900">{page.page || '/'}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                      {Math.round((page.views / analyticsData.total_visits) * 100)}%
                    </div>
                    <div className="text-lg font-bold text-blue-600">{page.views} views</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Referrers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Referrers</CardTitle>
            <CardDescription>Where your visitors are coming from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData?.top_referrers?.slice(0, 10).map((referrer, index) => (
                <div key={index} className="flex items-center justify-between border-b border-gray-200 pb-3 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold text-sm">
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-900">{referrer.referrer}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                      {Math.round((referrer.visits / analyticsData.total_visits) * 100)}%
                    </div>
                    <div className="text-lg font-bold text-green-600">{referrer.visits} visits</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Analytics;
